"use client"

import React, { createContext, useContext, ReactNode } from "react"

interface PermissionsContextType {
  permissions: string[]
  /** False until the session has resolved. Gate "access denied" UI on this so a refresh shows a loader, not a denial. */
  isReady: boolean
  hasPermission: (permission: string | string[]) => boolean
  hasAllPermissions: (permissions: string[]) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined)

interface PermissionsProviderProps {
  children: ReactNode
  permissions: string[]
  isReady: boolean
}

export function PermissionsProvider({ children, permissions, isReady }: PermissionsProviderProps) {
  const hasPermission = (permission: string | string[]) => {
    if (Array.isArray(permission)) {
      return permission.every(p => permissions.includes(p))
    }
    return permissions.includes(permission)
  }

  const hasAllPermissions = (requiredPermissions: string[]) => {
    return requiredPermissions.every(p => permissions.includes(p))
  }

  const hasAnyPermission = (requiredPermissions: string[]) => {
    return requiredPermissions.some(p => permissions.includes(p))
  }

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        isReady,
        hasPermission,
        hasAllPermissions,
        hasAnyPermission
      }}
    >
      {children}
    </PermissionsContext.Provider>
  )
}

export function usePermissions() {
  const context = useContext(PermissionsContext)
  if (!context) {
    throw new Error("usePermissions must be used within PermissionsProvider")
  }
  return context
}
